import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress } from "@ann/web-components";
import { listDrives } from "../../../../../services/drives";
import { listFoldersByTenant } from "../../../../../services/folders";
import {
  ModalWrapper,
  ModalContent,
  ModalBody,
  ModalFooter,
  InputLabel,
  HorizontalInputWrapper,
  FlexInputWrapper,
  FileTypeWrapper,
  StyledInput,
  StyledSelect,
  StepIndicator,
  TreeContainer,
  SelectedCount,
  Step2ContentWrapper,
  LoadingBackdrop,
} from "./SaveToDriveModal.styled";
import VaultFolderTreeView from "./components/VaultFolderTreeView";

const FILE_TYPES = [
  { value: "txt", label: "Text (.txt)", mimeType: "text/plain" },
  { value: "pdf", label: "PDF (.pdf)", mimeType: "application/pdf" },
  {
    value: "docx",
    label: "Word Document (.docx)",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
];

const SaveToDriveModal = ({ isOpen, onClose, onSave, documentTitle }) => {
  const [fileName, setFileName] = useState(documentTitle);
  const [selectedFileType, setSelectedFileType] = useState("txt");

  const [step, setStep] = useState(1);
  const [vaults, setVaults] = useState([]);
  const [expandedVaults, setExpandedVaults] = useState(new Set());
  const [vaultFolders, setVaultFolders] = useState({});
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [loadingVaults, setLoadingVaults] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFileName(documentTitle);
      setSelectedFileType("txt");
      setStep(1);
      setSelectedDestinations([]);
      setExpandedVaults(new Set());
      fetchVaults();
    }
  }, [isOpen, documentTitle]);

  const fetchVaults = async () => {
    setLoadingVaults(true);
    try {
      const baseMeta = {
        page: 1,
        page_size: 100,
        order_by: "created_at",
        order_direction: "DESC",
        filters: {},
      };

      const [userResp, manageResp] = await Promise.all([
        listDrives({ scope: "USER", meta: baseMeta }),
        listDrives({ scope: "MANAGE", meta: baseMeta }),
      ]);

      const userVaults = userResp?.data?.body || userResp?.data?.data || [];
      const manageVaults = manageResp?.data?.body || manageResp?.data?.data || [];
      const vaultsData = [...userVaults, ...manageVaults];

      const mapped = vaultsData.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        title: item.name || item.title || "Untitled Drive",
        name: item.name || item.title,
        vaultUuid: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        description: item.description,
        scope: item.scope,
        folderCount: item.folderCount || item.folders_count,
      }));

      setVaults(mapped);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching vaults:", error);
      setVaults([]);
    } finally {
      setLoadingVaults(false);
    }
  };

  const fetchFoldersForVault = async (vaultUuid) => {
    setLoadingFolders((prev) => ({ ...prev, [vaultUuid]: true }));
    try {
      const foldersBody = {
        parent_folder_uuid: null,
        vault_uuid: vaultUuid,
        page: 1,
        page_size: 100,
        order_by: ["updated_at"],
        order_direction: ["DESC"],
        filters: {},
        cursor: null,
      };

      const response = await listFoldersByTenant(foldersBody);
      const folders = response.data?.body || [];

      setVaultFolders((prev) => ({ ...prev, [vaultUuid]: folders }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching folders for vault ${vaultUuid}:`, error);
      setVaultFolders((prev) => ({ ...prev, [vaultUuid]: [] }));
    } finally {
      setLoadingFolders((prev) => ({ ...prev, [vaultUuid]: false }));
    }
  };

  const handleToggleVault = (vaultUuid) => {
    const newExpanded = new Set(expandedVaults);
    if (newExpanded.has(vaultUuid)) {
      newExpanded.delete(vaultUuid);
    } else {
      newExpanded.add(vaultUuid);
      if (!vaultFolders[vaultUuid]) {
        fetchFoldersForVault(vaultUuid);
      }
    }
    setExpandedVaults(newExpanded);
  };

  const handleToggleDestination = (destination) => {
    setSelectedDestinations((prev) => {
      const exists = prev.some(
        (d) =>
          d.vaultUuid === destination.vaultUuid &&
          d.folderUuid === destination.folderUuid
      );

      if (exists) {
        return prev.filter(
          (d) =>
            !(
              d.vaultUuid === destination.vaultUuid &&
              d.folderUuid === destination.folderUuid
            )
        );
      }
      return [...prev, destination];
    });
  };

  if (!isOpen) return null;

  const handleNext = (e) => {
    e.stopPropagation();
    if (!fileName.trim()) return;
    setStep(2);
  };

  const handleBack = (e) => {
    e.stopPropagation();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.stopPropagation();
    if (!fileName.trim() || selectedDestinations.length === 0) return;

    setIsSubmitting(true);
    try {
      const selectedType = FILE_TYPES.find(
        (type) => type.value === selectedFileType
      );
      await onSave(fileName.trim(), selectedType, selectedDestinations);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <ModalWrapper onClick={(e) => e.stopPropagation()}>
      <ModalContent role="dialog" onClick={(e) => e.stopPropagation()}>
        <StepIndicator>Step {step} of 2</StepIndicator>

        <ModalBody>
          {step === 1 ? (
            <HorizontalInputWrapper>
              <FlexInputWrapper>
                <InputLabel>File Name</InputLabel>
                <StyledInput
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="Enter file name"
                  disabled={isSubmitting}
                  onClick={(e) => e.stopPropagation()}
                />
              </FlexInputWrapper>
              <FileTypeWrapper>
                <InputLabel>File Type</InputLabel>
                <StyledSelect
                  value={selectedFileType}
                  onChange={(e) => setSelectedFileType(e.target.value)}
                  disabled={isSubmitting}
                  onClick={(e) => e.stopPropagation()}
                >
                  {FILE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </StyledSelect>
              </FileTypeWrapper>
            </HorizontalInputWrapper>
          ) : (
            <Step2ContentWrapper>
              <SelectedCount>
                {selectedDestinations.length} destination
                {selectedDestinations.length !== 1 ? "s" : ""} selected
              </SelectedCount>

              <TreeContainer>
                {loadingVaults ? (
                  <div
                    style={{ padding: "20px", textAlign: "center" }}
                  >
                    Loading vaults...
                  </div>
                ) : (
                  <VaultFolderTreeView
                    vaults={vaults}
                    vaultFolders={vaultFolders}
                    expandedVaults={expandedVaults}
                    selectedDestinations={selectedDestinations}
                    loadingFolders={loadingFolders}
                    onToggleVault={handleToggleVault}
                    onToggleDestination={handleToggleDestination}
                  />
                )}
              </TreeContainer>
              {isSubmitting && (
                <LoadingBackdrop aria-hidden="true">
                  <CircularProgress size="xl" />
                </LoadingBackdrop>
              )}
            </Step2ContentWrapper>
          )}
        </ModalBody>

        <ModalFooter>
          {step === 1 ? (
            <>
              <Button
                label="Cancel"
                size="medium"
                type="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              />
              <Button
                label="Next"
                size="medium"
                type="gradient"
                onClick={handleNext}
                disabled={!fileName.trim()}
              />
            </>
          ) : (
            <>
              <Button
                label="Back"
                size="medium"
                type="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              />
              <Button
                label={
                  isSubmitting
                    ? "Saving..."
                    : `Save to ${selectedDestinations.length} location${
                        selectedDestinations.length !== 1 ? "s" : ""
                      }`
                }
                size="medium"
                type="gradient"
                onClick={handleSubmit}
                disabled={
                  isSubmitting || selectedDestinations.length === 0
                }
              />
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalWrapper>
  );
};

SaveToDriveModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  documentTitle: PropTypes.string.isRequired,
};

export default SaveToDriveModal;

